import React, { useCallback, useMemo } from 'react';
import { TFunction } from 'i18next';
import { IMonitoringToolkit } from '@components-clean/core/monitoring-toolkit/interface';
import { IUnsavedChangesContext } from '@contexts-clean/unsaved-changes-context';
import MonitoringToolkitWrapper, {
    MonitoringToolkitWrapperProps,
} from '@pages-clean/central-monitoring/monitoring-toolkit/wrapper';
import ManageReviewsTab from '@pages-clean/risk-assessment/monitoring-toolkit/manage-reviews-tab';
import RiskAssessmentTagGrid from '@pages-clean/risk-assessment/monitoring-toolkit/risk-assessment-tag-grid';
import LocalStorage from '@utils-clean/local-storage-service';
import { IUser, Permissions } from '@contexts/user-context';
import { useAuthorization } from '@hooks/index';
import Constants from '@utils/constants';

export const riskAssessmentToolkitHelper: IMonitoringToolkit = {
    TOOLKIT_STATE_KEY: 'RiskAssessmentToolKit.OpenState',

    BOTTOM_PANEL_MIN_SIZE: 100,

    COLLAPSED_SIZE: '0fr',

    AUTO_SIZE: '1fr',

    translationKey: 'sm',

    canUserViewToolkit(user: IUser, moduleId: number) {
        // TEMPORARY: Manage Tags is currently hidden (see tabsContent below), leaving Manage Reviews
        // as the toolkit's only possible tab. This check exists only so the toolkit doesn't show up
        // empty for users without access to Manage Reviews. Remove this check (revert to always
        // returning true) once Manage Tags is re-enabled.
        return (
            user?.can?.({
                module: moduleId,
                element: Constants.PageElements.RiskReports,
                permission: Permissions.Enabled,
            }) ?? false
        );
    },

    canEditToolkit(user: IUser) {
        return (
            user?.canWrite?.({
                module: Constants.Modules.RAM,
                element: Constants.DataElements.RiskAssessment,
                permission: Permissions.ReadWrite,
            }) ?? false
        );
    },

    tabsContent() {
        return [];
    },

    clearMonitoringToolKitOpenState(user: IUser) {
        LocalStorage.removeItem(user, this.TOOLKIT_STATE_KEY);
    },

    hasUnsavedChanges(unsavedChangesContext: IUnsavedChangesContext) {
        return unsavedChangesContext.anyRiskAssessmentToolkitHasChanges();
    },

    clearUnsavedChanges(unsavedChangesContext: IUnsavedChangesContext) {
        unsavedChangesContext.riskAssessmentToolkitDiscardChanges();
    },
};

type Props = Omit<MonitoringToolkitWrapperProps, 'toolkitHelper'>;

export const RiskAssessmentToolkit = (props: Props) => {
    const user = useAuthorization();
    const canViewManageReviews =
        user?.can?.({
            module: Constants.Modules.RAM,
            element: Constants.PageElements.RiskReports,
            permission: Permissions.Enabled,
        }) ?? false;
    const tabsContent: IMonitoringToolkit['tabsContent'] = useCallback(
        (
            organisationKey: string,
            studyRef: string,
            studyNumber: string,
            canEdit: boolean,
            isHalfScreen: boolean,
            _datasetId: number,
            setSelectedTab,
            t: TFunction,
            study,
        ) => {
            return [
                {
                    title: t('manageReviews'),
                    shortTitle: t('manageReviewsShort'),
                    content: <ManageReviewsTab studyRef={studyRef} studyNumber={studyNumber} study={study} />,
                    visible: canViewManageReviews,
                },
                {
                    title: t('manageTags'),
                    shortTitle: t('manageTagsShort'),
                    content: <RiskAssessmentTagGrid canEdit={canEdit} studyRef={studyRef} />,
                    visible: false, // Once enabled, review canUserViewToolkit above.
                },
                // Include tabs where `visible` is either true or undefined
            ].filter((tab) => tab.visible);
        },
        [canViewManageReviews],
    );

    const toolkitHelper: IMonitoringToolkit = useMemo(
        () => ({
            ...riskAssessmentToolkitHelper,
            tabsContent,
        }),
        [tabsContent],
    );

    return <MonitoringToolkitWrapper {...props} toolkitHelper={toolkitHelper} />;
};

export const RiskAssessmentToolkitContexts = ({ children }: { children?: React.ReactNode }) => {
    return <>{children}</>;
};
